<template>
    <div class="import-url">
        <header class="import-head">
            <h1>{{ t('Import from URL', 'URL இருந்து இறக்குமதி') }}</h1>
            <p>{{ t('Paste a video link and we\'ll download and process it into the library.',
                'ஒரு காணொளி இணைப்பை ஒட்டவும், அதை நூலகத்திற்குள் பதிவிறக்கம் செய்து செயலாக்குவோம்.') }}</p>
        </header>

        <!-- Form -->
        <form v-if="!createdVideoId" class="import-form" @submit.prevent="submitImport">
            <label class="field">
                <span class="field-label">{{ t('Video URL', 'காணொளி URL') }} <em>*</em></span>
                <input
                    v-model.trim="sourceUrl"
                    type="url"
                    class="field-input"
                    placeholder="https://example.com/watch/..."
                    :disabled="submitting"
                    required
                />
                <span v-if="isYouTubeUrl" class="field-hint warn">
                    <vue-feather type="alert-triangle" size="14"></vue-feather>
                    {{ t('YouTube imports need server-side cookies configured, or they will fail. Non-YouTube links work directly.',
                        'YouTube இறக்குமதிகளுக்கு சர்வர் குக்கீஸ் தேவை, இல்லையெனில் தோல்வியடையும்.') }}
                </span>
            </label>

            <div class="field-row">
                <label class="field">
                    <span class="field-label">{{ t('Title (English)', 'தலைப்பு (ஆங்கிலம்)') }}</span>
                    <input v-model.trim="titleEN" type="text" class="field-input" :disabled="submitting" />
                </label>
                <label class="field">
                    <span class="field-label">{{ t('Title (Tamil)', 'தலைப்பு (தமிழ்)') }}</span>
                    <input v-model.trim="titleTA" type="text" class="field-input" :disabled="submitting" />
                </label>
            </div>
            <p class="field-hint">{{ t('Enter at least one title.', 'குறைந்தது ஒரு தலைப்பையாவது உள்ளிடவும்.') }}</p>

            <div class="field-row">
                <label class="field">
                    <span class="field-label">{{ t('Category', 'வகை') }}</span>
                    <select v-model="primaryCategoryId" class="field-input" :disabled="submitting || loadingCategories">
                        <option :value="null">{{ t('— None —', '— எதுவுமில்லை —') }}</option>
                        <option v-for="cat in primaryCategories" :key="cat.id" :value="cat.id">
                            {{ currentLanguage === 'TA' ? cat.nameTA : cat.nameEN }}
                        </option>
                    </select>
                </label>
                <label class="field" v-if="secondaryOptions.length">
                    <span class="field-label">{{ t('Subcategory', 'துணை வகை') }}</span>
                    <select v-model="secondaryCategoryId" class="field-input" :disabled="submitting">
                        <option :value="null">{{ t('— None —', '— எதுவுமில்லை —') }}</option>
                        <option v-for="sub in secondaryOptions" :key="sub.id" :value="sub.id">
                            {{ currentLanguage === 'TA' ? sub.nameTA : sub.nameEN }}
                        </option>
                    </select>
                </label>
            </div>

            <label class="field">
                <span class="field-label">{{ t('Description (English)', 'விவரம் (ஆங்கிலம்)') }}</span>
                <textarea v-model.trim="descriptionEN" class="field-input" rows="2" :disabled="submitting"></textarea>
            </label>

            <p v-if="errorMessage" class="form-error">
                <vue-feather type="alert-circle" size="16"></vue-feather> {{ errorMessage }}
            </p>

            <button type="submit" class="submit-btn" :disabled="!canSubmit || submitting">
                <vue-feather v-if="submitting" type="loader" animation="spin" size="16"></vue-feather>
                <vue-feather v-else type="download" size="16"></vue-feather>
                {{ submitting ? t('Submitting…', 'சமர்ப்பிக்கிறது…') : t('Import video', 'காணொளியை இறக்குமதி செய்') }}
            </button>
        </form>

        <!-- Progress / result -->
        <div v-else class="import-status" :class="statusKind">
            <vue-feather v-if="statusKind === 'working'" type="loader" animation="spin" size="30"></vue-feather>
            <vue-feather v-else-if="statusKind === 'done'" type="check-circle" size="30"></vue-feather>
            <vue-feather v-else type="x-circle" size="30"></vue-feather>

            <h2>{{ statusTitle }}</h2>
            <p class="status-message">{{ statusMessage }}</p>

            <div class="status-actions">
                <router-link v-if="statusKind !== 'failed'" :to="`/edit/${createdVideoId}`" class="ghost-btn">
                    <vue-feather type="edit-2" size="15"></vue-feather>
                    {{ t('Edit details', 'விவரங்களைத் திருத்து') }}
                </router-link>
                <button class="ghost-btn" @click="resetForm">
                    <vue-feather type="plus" size="15"></vue-feather>
                    {{ t('Import another', 'மற்றொன்றை இறக்குமதி செய்') }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { API } from 'aws-amplify';
import { Storage } from '@aws-amplify/storage';
import ShortUniqueId from 'short-unique-id';
import { createVideoModel } from '../graphql/mutations';
import { listCategoryModelsLite, getVideoProcessingStatus, getUserModelByLegacyId } from '../graphql/custom';

const uid = new ShortUniqueId();
const DONE_STATUSES = ['processingReady', 'ready'];
const POLL_INTERVAL_MS = 5000;
const POLL_MAX_TICKS = 240; // ~20 minutes

export default {
    name: 'AdminImportUrl',
    inject: {
        languageRef: { from: 'currentLanguage', default: 'EN' },
        currentUserRef: { from: 'currentUserRef', default: null },
    },
    data() {
        return {
            sourceUrl: '',
            titleEN: '',
            titleTA: '',
            descriptionEN: '',
            categories: [],
            loadingCategories: false,
            primaryCategoryId: null,
            secondaryCategoryId: null,
            submitting: false,
            errorMessage: '',
            createdVideoId: null,
            status: '',
            statusMessage: '',
            pollTimer: null,
            pollTicks: 0,
        };
    },
    computed: {
        currentLanguage() {
            return this.languageRef || 'EN';
        },
        // Named authUser (not currentUser) to avoid colliding with the global
        // app.mixin's `currentUser` data property, which would shadow this.
        authUser() {
            return this.currentUserRef || null;
        },
        primaryCategories() {
            // Exclude any category that is a child of another (only top-level as primary).
            const childIds = new Set();
            this.categories.forEach(c => (c.children?.items || []).forEach(ch => childIds.add(ch.id)));
            return this.categories.filter(c => !childIds.has(c.id));
        },
        secondaryOptions() {
            const parent = this.categories.find(c => c.id === this.primaryCategoryId);
            return parent?.children?.items || [];
        },
        isYouTubeUrl() {
            return /(^|\.)(youtube\.com|youtu\.be)$/i.test(this.parsedHost);
        },
        parsedHost() {
            try {
                return new URL(this.sourceUrl).hostname;
            } catch (e) {
                return '';
            }
        },
        canSubmit() {
            return !!this.parsedHost && (!!this.titleEN || !!this.titleTA);
        },
        statusKind() {
            if (this.status === 'failed') return 'failed';
            if (DONE_STATUSES.includes(this.status)) return 'done';
            return 'working';
        },
        statusTitle() {
            if (this.statusKind === 'done') return this.t('Video imported', 'காணொளி இறக்குமதி செய்யப்பட்டது');
            if (this.statusKind === 'failed') return this.t('Import failed', 'இறக்குமதி தோல்வியடைந்தது');
            return this.t('Processing…', 'செயலாக்குகிறது…');
        },
    },
    watch: {
        primaryCategoryId() {
            // Reset subcategory when the parent changes.
            this.secondaryCategoryId = null;
        },
    },
    mounted() {
        this.loadCategories();
    },
    beforeUnmount() {
        this.stopPolling();
    },
    methods: {
        t(en, ta) {
            return this.currentLanguage === 'TA' ? ta : en;
        },
        async loadCategories() {
            this.loadingCategories = true;
            try {
                const res = await API.graphql({ query: listCategoryModelsLite, variables: { limit: 200 } });
                this.categories = res.data.listCategoryModels.items || [];
            } catch (err) {
                console.error('Failed to load categories', err);
            } finally {
                this.loadingCategories = false;
            }
        },
        async submitImport() {
            this.errorMessage = '';
            if (!this.canSubmit) {
                this.errorMessage = this.t('Enter a valid URL and at least one title.',
                    'சரியான URL மற்றும் குறைந்தது ஒரு தலைப்பையாவது உள்ளிடவும்.');
                return;
            }

            // Resolve the UserModel id (the owner FK). For legacy users this is
            // their legacy UserModel id, NOT their Cognito sub — a sub-keyed lookup
            // would attach the video to a different (duplicate, avatar-less) profile.
            // Mirror App.vue's order: resolved model id -> lookup by legacyId -> sub.
            let ownerId = this.authUser?.model?.id || null;
            if (!ownerId) {
                try {
                    const cognitoUser = await API.Auth.currentAuthenticatedUser();
                    const legacyUserId = cognitoUser?.attributes?.['custom:legacyUserId'];
                    if (legacyUserId) {
                        const res = await API.graphql({
                            query: getUserModelByLegacyId,
                            variables: { legacyId: legacyUserId },
                        });
                        ownerId = res.data.userByLegacyId?.items?.[0]?.id || null;
                    }
                    // Last resort for non-legacy users (their UserModel.id === sub).
                    if (!ownerId) {
                        ownerId = cognitoUser?.attributes?.sub || null;
                    }
                } catch (authErr) {
                    console.error('Could not resolve Cognito user', authErr);
                }
            }
            if (!ownerId) {
                this.errorMessage = this.t('Could not determine your account. Please re-login.',
                    'உங்கள் கணக்கை அடையாளம் காண முடியவில்லை. மீண்டும் உள்நுழையவும்.');
                return;
            }

            this.submitting = true;
            const videoId = uid();
            try {
                await API.graphql({
                    query: createVideoModel,
                    variables: {
                        input: {
                            id: videoId,
                            titleEN: this.titleEN || '',
                            titleTA: this.titleTA || '',
                            descriptionEN: this.descriptionEN || '',
                            descriptionTA: '',
                            ownerId,
                            videoModelPrimaryCategoryId: this.primaryCategoryId || null,
                            videoModelSecondaryCategoryId: this.secondaryCategoryId || null,
                            externalVideoUrl: this.sourceUrl,
                            status: 'processing',
                            publishState: 'draft',
                        },
                    },
                    authMode: 'AMAZON_COGNITO_USER_POOLS',
                });

                // Hand off to the yt-dlp / Fargate pipeline via the url-request trigger.
                // requireApproval:false — admin imports process immediately.
                await Storage.put(
                    `videos/url-requests/${videoId}-${Date.now()}.json`,
                    JSON.stringify({
                        videoId,
                        sourceUrl: this.sourceUrl,
                        requireApproval: false,
                        requestedAt: new Date().toISOString(),
                    }),
                    { contentType: 'application/json' }
                );

                this.createdVideoId = videoId;
                this.status = 'processing';
                this.statusMessage = this.t('Downloading and processing the video. This can take a few minutes.',
                    'காணொளியைப் பதிவிறக்கி செயலாக்குகிறது. இதற்கு சில நிமிடங்கள் ஆகலாம்.');
                this.startPolling(videoId);
            } catch (err) {
                console.error('Import failed', err);
                this.errorMessage = this.t('Failed to submit the import. Please try again.',
                    'இறக்குமதியை சமர்ப்பிக்க முடியவில்லை. மீண்டும் முயற்சிக்கவும்.');
            } finally {
                this.submitting = false;
            }
        },
        startPolling(videoId) {
            this.stopPolling();
            this.pollTicks = 0;
            this.pollTimer = setInterval(() => this.pollStatus(videoId), POLL_INTERVAL_MS);
        },
        stopPolling() {
            if (this.pollTimer) {
                clearInterval(this.pollTimer);
                this.pollTimer = null;
            }
        },
        async pollStatus(videoId) {
            this.pollTicks += 1;
            if (this.pollTicks > POLL_MAX_TICKS) {
                this.stopPolling();
                this.statusMessage = this.t('Still processing — check the video later in Pending videos.',
                    'இன்னும் செயலாக்குகிறது — பின்னர் நிலுவை காணொளிகளில் பார்க்கவும்.');
                return;
            }
            try {
                const res = await API.graphql({
                    query: getVideoProcessingStatus,
                    variables: { id: videoId },
                });
                const video = res.data.getVideoModel;
                if (!video) return;
                this.status = video.status;

                if (video.status === 'thumbsReady') {
                    this.statusMessage = this.t('Preview generated, encoding video…',
                        'முன்னோட்டம் உருவாக்கப்பட்டது, காணொளியை குறியாக்குகிறது…');
                } else if (DONE_STATUSES.includes(video.status)) {
                    this.statusMessage = this.t('The video is ready. It\'s saved as a draft — edit its details or publish it.',
                        'காணொளி தயாராக உள்ளது. வரைவாக சேமிக்கப்பட்டது — விவரங்களைத் திருத்தவும் அல்லது வெளியிடவும்.');
                    this.stopPolling();
                } else if (video.status === 'failed') {
                    this.statusMessage = video.errorMessage
                        || this.t('The video could not be downloaded or processed.',
                            'காணொளியைப் பதிவிறக்க அல்லது செயலாக்க முடியவில்லை.');
                    this.stopPolling();
                }
            } catch (err) {
                console.error('Status poll failed', err);
            }
        },
        resetForm() {
            this.stopPolling();
            this.sourceUrl = '';
            this.titleEN = '';
            this.titleTA = '';
            this.descriptionEN = '';
            this.primaryCategoryId = null;
            this.secondaryCategoryId = null;
            this.errorMessage = '';
            this.createdVideoId = null;
            this.status = '';
            this.statusMessage = '';
        },
    },
};
</script>

<style scoped>
.import-url {
    background: var(--bg-card);
    border: 1px solid var(--border-color);
    border-radius: 10px;
    padding: 24px;
    max-width: 720px;
}

.import-head h1 {
    margin: 0 0 4px;
    font-size: 22px;
    color: var(--text-primary);
}

.import-head p {
    margin: 0 0 20px;
    color: var(--text-secondary);
    font-size: 14px;
}

.import-form {
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.field {
    display: flex;
    flex-direction: column;
    gap: 6px;
    flex: 1;
}

.field-row {
    display: flex;
    gap: 14px;
}

.field-label {
    font-size: 13px;
    font-weight: 600;
    color: var(--text-secondary);
}

.field-label em {
    color: #ff1314;
    font-style: normal;
}

.field-input {
    padding: 10px 12px;
    border: 1px solid var(--border-color);
    border-radius: 8px;
    background: var(--bg-primary);
    color: var(--text-primary);
    font-size: 14px;
    font-family: inherit;
}

.field-input:focus {
    outline: none;
    border-color: #ff1314;
}

.field-hint {
    font-size: 12px;
    color: var(--text-secondary);
    margin: -4px 0 0;
    display: flex;
    align-items: center;
    gap: 6px;
}

.field-hint.warn {
    color: #d98200;
}

.form-error {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #ff1314;
    font-size: 14px;
    margin: 0;
}

.submit-btn {
    align-self: flex-start;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 11px 20px;
    border: none;
    border-radius: 8px;
    background: #ff1314;
    color: #fff;
    font-size: 15px;
    font-weight: 700;
    cursor: pointer;
    transition: opacity 0.15s;
}

.submit-btn:disabled {
    opacity: 0.55;
    cursor: not-allowed;
}

.import-status {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 10px;
    padding: 30px 16px;
}

.import-status h2 {
    margin: 4px 0 0;
    font-size: 19px;
    color: var(--text-primary);
}

.status-message {
    margin: 0;
    color: var(--text-secondary);
    font-size: 14px;
    max-width: 440px;
}

.import-status.working { color: #ff1314; }
.import-status.done { color: #1aa251; }
.import-status.failed { color: #ff1314; }

.status-actions {
    display: flex;
    gap: 10px;
    margin-top: 12px;
}

.ghost-btn {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 9px 16px;
    border: 1px solid var(--border-color);
    border-radius: 8px;
    background: var(--bg-primary);
    color: var(--text-primary);
    font-size: 14px;
    font-weight: 600;
    text-decoration: none;
    cursor: pointer;
}

.ghost-btn:hover {
    background: var(--bg-hover);
}

@media (max-width: 640px) {
    .field-row {
        flex-direction: column;
    }
}
</style>
